import React from 'react';
import { Typography, IconButton, Grid, Tooltip } from '@mui/material';
import { round, startCase, toNumber } from 'lodash';
import { GridColDef } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';

import { formatCurrencyFloat, formatPercentageString } from 'shared/utils/numberFormatters';

import { ExpenseHistory, InputMethod } from 'types/underwriting/income-approach/expenses';
import { EXPENSE_AMOUNT_INPUT_METHOD, EXPENSE_RATIO_INPUT_METHOD } from 'shared/constants/expenses';

const safeFormatPercentageString = (value: number | null | undefined) => formatPercentageString(toNumber(value));

const getColumnValueFormatter = (params: any, inputMethod?: InputMethod) => {
  if (inputMethod === 'amount') {
    return params.isTotal ? safeFormatPercentageString : formatCurrencyFloat;
  }

  if (inputMethod === 'ratio') {
    return params.isTotal ? formatCurrencyFloat : safeFormatPercentageString;
  }

  return formatCurrencyFloat;
};

export const getBaseColumns = (
  expenseHistory: ExpenseHistory[],
  deleteColumn: (expenseHistoryId: string) => void,
  inputMethod?: InputMethod
) => {
  const baseColumns: GridColDef[] = [
    {
      field: 'category',
      headerName: 'Operating Expenses',
      width: 300,
      flex: 2,
      editable: false,
      sortable: false,
    },
  ];

  // For some reason, complains about `editable` being a function
  // @ts-ignore
  const yearCols: GridColDef[] = expenseHistory.map((expenseHistoryItem: ExpenseHistory) => ({
    field: `${expenseHistoryItem.period}-${expenseHistoryItem.year}`,
    headerName: `${expenseHistoryItem.year} ${startCase(expenseHistoryItem.period)}`,
    width: 150,
    type: 'number',
    editable: (params: any) => !params.row.isTotal,
    flex: 1,
    sortable: false,
    valueFormatter: (value: number | null | undefined, params: any) =>
      getColumnValueFormatter(params, inputMethod)(value),
    renderHeader: () => (
      <Grid container direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Grid item xs={10}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              wordWrap: 'wrap',
              whiteSpace: 'normal',
              overflow: 'hidden',
            }}
          >{`${expenseHistoryItem.year} ${startCase(expenseHistoryItem.period)}`}</Typography>
        </Grid>
        <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton sx={{ padding: 0.5 }} onClick={() => deleteColumn(expenseHistoryItem.id)}>
            <Tooltip title="Delete">
              <CloseIcon />
            </Tooltip>
          </IconButton>
        </Grid>
      </Grid>
    ),
  }));

  const endColumns: GridColDef[] = [
    {
      field: 'average',
      headerName: 'Average',
      width: 150,
      type: 'number',
      editable: false,
      sortable: false,
      valueFormatter: (value: number | null | undefined, params: any) =>
        getColumnValueFormatter(params, inputMethod)(value),
    },
  ];

  return [...baseColumns, ...yearCols, ...endColumns];
};

export function calculateOperatingExpenses(
  effectiveGrossIncome: number,
  currentValue: number,
  inputMethod: InputMethod
): { amount: number; ratio: number } {
  if (!effectiveGrossIncome) {
    return inputMethod === EXPENSE_AMOUNT_INPUT_METHOD
      ? { amount: currentValue, ratio: 0 }
      : { amount: 0, ratio: currentValue };
  }

  let amount = 0;
  let ratio = 0;

  if (inputMethod === EXPENSE_AMOUNT_INPUT_METHOD) {
    amount = currentValue;
    ratio = (amount / effectiveGrossIncome) * 100;
  } else if (inputMethod === EXPENSE_RATIO_INPUT_METHOD) {
    ratio = currentValue;
    amount = (effectiveGrossIncome * ratio) / 100;
  }

  return { amount: round(amount, 2), ratio: round(ratio, 4) };
}
